import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ToastHostService } from './toast-host.service';

/**
 * "Persiana" (blind) toast host. A single fixed anchor mounted by the app
 * shells just below the header. Its `overflow: hidden` is the clip mask: the
 * V4 card sits above the clip line (behind the header) and slides DOWN into
 * view on show, then retracts UP behind the header on close.
 *
 * The card markup + styles are the SAME V4 white card as the former
 * MatSnackBar-hosted ToastComponent — only the delivery mechanic (custom
 * fixed host + slide animation) is new. Driven by the single-slot
 * {@link ToastHostService}.
 */
@Component({
  selector: 'app-toast-host',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (store.current(); as toast) {
      <div class="toast-persiana-anchor">
        <div class="v4-toast" [class]="toast.type" [class.show]="store.show()">
          <div class="v4-toast-body">
            <div class="v4-toast-icon">
              @switch (toast.type) {
                @case ('success') {
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                }
                @case ('error') {
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                }
                @case ('warning') {
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                }
                @default {
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                }
              }
            </div>
            <div class="v4-toast-content">
              <p class="v4-toast-text">{{ toast.message }}</p>
              @if (toast.subtitle) {
                <p class="v4-toast-sub">{{ toast.subtitle }}</p>
              }
            </div>
            <button class="v4-toast-ok" type="button" (click)="store.dismiss(toast.id)">OK</button>
          </div>
          <div class="v4-toast-progress" [style.animation-duration.ms]="toast.duration"></div>
        </div>
      </div>
    }
  `,
  styles: [`
    :host {
      display: contents;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }

    /* ── Persiana mechanic (NEW) ── */
    .toast-persiana-anchor {
      position: fixed;
      top: var(--toast-header-top, 0px); /* header bottom; set by the shells */
      left: 50%;
      transform: translateX(-50%);
      z-index: 1000; /* matches the former MatSnackBar overlay */
      overflow: hidden; /* CLIP MASK = the persiana effect */
      padding: 8px 0 28px; /* small top gap + room for the card shadow */
      margin-bottom: -28px;
      pointer-events: none;
    }
    .v4-toast {
      pointer-events: auto;
      transform: translateY(calc(-100% - 16px)); /* hidden above the clip line (behind the header) */
      transition: transform 0.45s cubic-bezier(0.34, 1.3, 0.64, 1);
    }
    .v4-toast.show { transform: translateY(0); }
    /* progress runs only while shown */
    .v4-toast:not(.show) .v4-toast-progress { animation: none; }
    @media (prefers-reduced-motion: reduce) {
      .v4-toast { transition: none; }
    }

    /* ── V4 card (UNCHANGED — verbatim from toast.component.ts) ── */
    .v4-toast {
      width: 380px;
      max-width: 90vw;
      background: #fff;
      border: 1px solid #ebe6df;
      border-radius: 12px;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
      overflow: hidden;
    }
    .v4-toast-body {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 13px 16px;
    }
    .v4-toast-icon {
      width: 30px;
      height: 30px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .v4-toast-icon svg { width: 15px; height: 15px; }
    .v4-toast-content { flex: 1; min-width: 0; }
    .v4-toast-text {
      margin: 0;
      font-size: 13px;
      font-weight: 600;
      color: #2c2c2c;
      line-height: 1.35;
      overflow-wrap: anywhere;
    }
    .v4-toast-sub {
      margin: 1px 0 0;
      font-size: 11px;
      color: #999;
      line-height: 1.3;
      overflow-wrap: anywhere;
    }
    .v4-toast-ok {
      font-size: 12px;
      font-weight: 700;
      background: none;
      border: none;
      cursor: pointer;
      padding: 6px 14px;
      border-radius: 7px;
      font-family: inherit;
      flex-shrink: 0;
      transition: background 0.12s ease;
    }
    .v4-toast-progress {
      height: 3px;
      transform-origin: left;
      animation-name: v4-toast-shrink;
      animation-timing-function: linear;
      animation-fill-mode: forwards;
    }
    @keyframes v4-toast-shrink {
      from { transform: scaleX(1); }
      to { transform: scaleX(0); }
    }

    /* success */
    .v4-toast.success .v4-toast-icon { background: #f0f7f3; color: #3d7056; }
    .v4-toast.success .v4-toast-ok { color: #3d7056; }
    .v4-toast.success .v4-toast-ok:hover { background: #f0f7f3; }
    .v4-toast.success .v4-toast-progress { background: #3d7056; }

    /* error */
    .v4-toast.error .v4-toast-icon { background: #fdf5f4; color: #c0564b; }
    .v4-toast.error .v4-toast-ok { color: #c0564b; }
    .v4-toast.error .v4-toast-ok:hover { background: #fdf5f4; }
    .v4-toast.error .v4-toast-progress { background: #c0564b; }

    /* warning */
    .v4-toast.warning .v4-toast-icon { background: #fef5f0; color: #ef6031; }
    .v4-toast.warning .v4-toast-ok { color: #ef6031; }
    .v4-toast.warning .v4-toast-ok:hover { background: #fef5f0; }
    .v4-toast.warning .v4-toast-progress { background: #ef6031; }

    /* info */
    .v4-toast.info .v4-toast-icon { background: #eef3fb; color: #4a72a8; }
    .v4-toast.info .v4-toast-ok { color: #4a72a8; }
    .v4-toast.info .v4-toast-ok:hover { background: #eef3fb; }
    .v4-toast.info .v4-toast-progress { background: #4a72a8; }
  `]
})
export class ToastHostComponent {
  readonly store = inject(ToastHostService);
}
